import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi } from '@/lib/api';

function defaultRange() {
  const now = new Date();
  const from = new Date(now.getTime() - 30 * 86400000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(now) };
}

export function CostPage() {
  const def = defaultRange();
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const range = { from, to };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold tracking-tight">成本</h2>
      <p className="text-sm text-muted-foreground">
        所有 cost USD 来自 <code className="text-xs bg-muted px-1 rounded">model_pricing.cost_usd_per_1m_*</code>，
        credits 按固定规则 <code className="text-xs bg-muted px-1 rounded">1 credit = $0.0001</code> 换算。
      </p>

      <Card>
        <CardContent className="flex gap-3 pt-6">
          <Field label="From (date)">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To (date)">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      <Summary range={range} />
      <ByModel />
      <ByUser range={range} />
      <Daily range={range} />
    </div>
  );
}

function Summary({ range }: { range: { from: string; to: string } }) {
  const { data, isLoading } = useQuery({
    queryKey: ['cost', 'summary', range],
    queryFn: () => adminApi.costSummary(range),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">区间总览</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="活跃用户" value={data.total_users.toLocaleString()} />
            <Stat label="请求数" value={data.total_requests.toLocaleString()} />
            <Stat label="总 credit" value={Math.round(data.total_credits).toLocaleString()} />
            <Stat label="总 cost USD" value={`$${data.total_cost_usd.toFixed(4)}`} />
            <Stat label="input token" value={data.total_input_tokens.toLocaleString()} />
            <Stat label="cached token" value={data.total_cached_tokens.toLocaleString()} />
            <Stat label="output token" value={data.total_output_tokens.toLocaleString()} />
            <Stat label="cache 命中率" value={`${data.cache_hit_pct}%`} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ByModel() {
  const { data, isLoading } = useQuery({ queryKey: ['cost', 'by-model'], queryFn: adminApi.costByModel });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">按模型（全局，无时间过滤）</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <Table>
            <THead>
              <TR>
                <TH>model</TH>
                <TH>请求数</TH>
                <TH>用户数</TH>
                <TH>input</TH>
                <TH>cached</TH>
                <TH>output</TH>
                <TH>credit</TH>
                <TH>USD 成本</TH>
              </TR>
            </THead>
            <TBody>
              {data.map((r) => (
                <TR key={r.model}>
                  <TD className="font-mono text-xs">{r.model}</TD>
                  <TD>{r.request_count}</TD>
                  <TD>{r.unique_users}</TD>
                  <TD className="text-xs">{r.input_tokens.toLocaleString()}</TD>
                  <TD className="text-xs">{r.cached_tokens.toLocaleString()}</TD>
                  <TD className="text-xs">{r.output_tokens.toLocaleString()}</TD>
                  <TD>{Math.round(r.credits).toLocaleString()}</TD>
                  <TD className="font-mono">${r.cost_usd.toFixed(6)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ByUser({ range }: { range: { from: string; to: string } }) {
  const { data, isLoading } = useQuery({
    queryKey: ['cost', 'by-user', range],
    queryFn: () => adminApi.costByUser({ ...range, limit: 50 }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">按用户 Top 50（按 USD 成本排序）</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <Table>
            <THead>
              <TR>
                <TH>用户</TH>
                <TH>请求</TH>
                <TH>input</TH>
                <TH>cached</TH>
                <TH>output</TH>
                <TH>credit</TH>
                <TH>USD</TH>
              </TR>
            </THead>
            <TBody>
              {data.map((r) => (
                <TR key={r.user_id}>
                  <TD>
                    <div className="text-xs">{r.user_phone ? `手机号：${r.user_phone}` : '手机号未设置'}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.user_id.slice(0, 8)}…</div>
                  </TD>
                  <TD>{r.request_count}</TD>
                  <TD className="text-xs">{r.input_tokens.toLocaleString()}</TD>
                  <TD className="text-xs">{r.cached_tokens.toLocaleString()}</TD>
                  <TD className="text-xs">{r.output_tokens.toLocaleString()}</TD>
                  <TD>{Math.round(r.credits).toLocaleString()}</TD>
                  <TD className="font-mono">${r.cost_usd.toFixed(6)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Daily({ range }: { range: { from: string; to: string } }) {
  const { data, isLoading } = useQuery({
    queryKey: ['cost', 'daily', range],
    queryFn: () => adminApi.costDaily(range),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">每日（按 Asia/Shanghai 日期分桶）</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <Table>
            <THead>
              <TR>
                <TH>day</TH>
                <TH>用户数</TH>
                <TH>请求</TH>
                <TH>credit</TH>
                <TH>USD</TH>
              </TR>
            </THead>
            <TBody>
              {data.map((r) => (
                <TR key={r.day}>
                  <TD className="font-mono text-xs">{r.day}</TD>
                  <TD>{r.unique_users}</TD>
                  <TD>{r.request_count}</TD>
                  <TD>{Math.round(r.credits).toLocaleString()}</TD>
                  <TD className="font-mono">${r.cost_usd.toFixed(6)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold font-mono">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
